import { QueryClient } from "@tanstack/react-query";

// Create query client with performance-optimized defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time - data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Cache time - data stays in cache for 30 minutes after component unmounts
      gcTime: 30 * 60 * 1000,
      // Retry failed requests with exponential backoff
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 3 times for network/server errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Background refetching for fresh data
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Refetch interval for critical data (disabled by default, enabled per query)
      refetchInterval: false,
      // Enable background refetching while stale
      refetchOnMount: true,
      // Network mode for better offline handling
      networkMode: "online",
    },
    mutations: {
      // Retry failed mutations once with delay
      retry: (failureCount, error: any) => {
        // Don't retry client errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 1;
      },
      retryDelay: 1000,
      // Network mode for mutations
      networkMode: "online",
    },
  },
});

// Prefetch common data on app start
export const prefetchCommonData = async () => {
  // Prefetch organization data as it's needed on most pages
  queryClient.prefetchQuery({
    queryKey: queryKeys.organization(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Prefetch booking flows list
  queryClient.prefetchQuery({
    queryKey: queryKeys.bookingFlows(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Query key factory for consistent cache keys and invalidation
export const queryKeys = {
  // Booking flows
  bookingFlows: () => ["booking-flows"] as const,
  bookingFlow: (slug: string) => ["booking-flow", slug] as const,

  // Team members with hierarchical keys for better invalidation
  teamMembers: (flowSlug: string) => ["team-members", flowSlug] as const,
  allTeamMembers: () => ["team-members"] as const,

  // Organization with sub-keys
  organization: () => ["organization"] as const,
  organizationUsers: () => ["organization", "users"] as const,
  organizationStats: () => ["organization", "stats"] as const,

  // Analytics with hierarchical structure
  analytics: (params: Record<string, any>) => ["analytics", params] as const,
  analyticsBase: () => ["analytics"] as const,

  // Calendar preview with parameters
  calendarPreview: (slug: string, params: Record<string, any>) =>
    ["calendar-preview", slug, params] as const,
  calendarPreviewBase: (slug: string) => ["calendar-preview", slug] as const,
} as const;

// Helper function to invalidate related queries
export const invalidateQueries = {
  bookingFlows: (tenant?: string) => {
    if (tenant) {
      queryClient.invalidateQueries({
        queryKey: ["booking-flows", tenant],
      });
    } else {
      queryClient.invalidateQueries({ queryKey: ["booking-flows"] });
    }
  },
  teamMembers: (tenant?: string, flowSlug?: string) => {
    if (tenant && flowSlug) {
      queryClient.invalidateQueries({
        queryKey: ["team-members", tenant, "flow", flowSlug],
      });
    } else if (tenant) {
      queryClient.invalidateQueries({ queryKey: ["team-members", tenant] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
    }
  },
  organization: (tenant?: string) => {
    if (tenant) {
      queryClient.invalidateQueries({
        queryKey: ["organization", tenant],
      });
    } else {
      queryClient.invalidateQueries({ queryKey: ["organization"] });
    }
  },
  analytics: (tenant?: string) => {
    if (tenant) {
      queryClient.invalidateQueries({ queryKey: ["analytics", tenant] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["analytics"] });
    }
  },
  calendarPreview: (tenant?: string, slug?: string) => {
    if (tenant && slug) {
      queryClient.invalidateQueries({
        queryKey: ["calendar-preview", tenant, "flow", slug],
      });
    } else if (tenant) {
      queryClient.invalidateQueries({ queryKey: ["calendar-preview", tenant] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["calendar-preview"] });
    }
  },
  // Invalidate all tenant-specific data when switching tenants
  allTenantData: (tenant?: string) => {
    if (tenant) {
      // Invalidate specific tenant data
      queryClient.invalidateQueries({ queryKey: ["organization", tenant] });
      queryClient.invalidateQueries({ queryKey: ["booking-flows", tenant] });
      queryClient.invalidateQueries({ queryKey: ["team-members", tenant] });
      queryClient.invalidateQueries({ queryKey: ["analytics", tenant] });
      queryClient.invalidateQueries({ queryKey: ["calendar-preview", tenant] });
    } else {
      // Invalidate all tenant data (fallback)
      queryClient.invalidateQueries({ queryKey: ["organization"] });
      queryClient.invalidateQueries({ queryKey: ["booking-flows"] });
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      queryClient.invalidateQueries({ queryKey: ["analytics"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-preview"] });
    }
  },
} as const;

// Helper function to clear all cached data (useful for tenant switching)
export const clearAllQueries = () => {
  queryClient.clear();
};
